/**
 * GA4 analytics for the fantasy SPA.
 *
 * The React app (everything under /app) historically fired NOTHING — only the
 * static marketing landing (index.php) carried the tag. This wires the same
 * shared GA4 property the rest of DraftEdge uses (G-MEWCEVEL81) into the app so
 * pageviews + product events land in ONE property, with the cross-domain linker
 * so an in-app "Get the Kit" hop to draftedge.com stays a single session.
 *
 * Reusing the same measurement ID across draftedge.com + fantasy.draftedge.com
 * is fully supported by GA4 (that's what the linker + `accept_incoming` are for)
 * — it is NOT a policy problem the way sharing an AdSense unit would be.
 *
 * Gated to real *.draftedge.com hosts, so localhost dev and the Capacitor
 * native WebView (served from capacitor://) never pollute production analytics.
 * When disabled, every function is a safe no-op.
 */

const GA_MEASUREMENT_ID = 'G-MEWCEVEL81';
const LINKER_DOMAINS = ['draftedge.com', 'fantasy.draftedge.com'];

type GtagArgs =
  | ['js', Date]
  | ['config', string, Record<string, unknown>?]
  | ['set', string, Record<string, unknown>]
  | ['event', string, Record<string, unknown>?];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

let initialized = false;

/** Only track on the live web hosts — never localhost or the native app. */
export function analyticsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'draftedge.com' || h.endsWith('.draftedge.com');
}

/**
 * Inject gtag.js once and configure the shared property with the cross-domain
 * linker. Page views are sent manually (send_page_view:false) because this is a
 * client-routed SPA — gtag's auto pageview only fires on the initial HTML load.
 */
export function initAnalytics(): void {
  if (initialized || !analyticsEnabled()) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  // Use the exact arguments object shape gtag expects (not a rest array).
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  } as Window['gtag'];

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.gtag!('js', new Date());
  window.gtag!('set', 'linker', {
    domains: LINKER_DOMAINS,
    accept_incoming: true,
  });
  window.gtag!('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

/**
 * First-party journey beacon (draftedge.com/api/t.php). GA4 loses most of
 * this audience to blockers; the beacon is what the admin's conversion
 * section reads. Same-site (both hosts are *.draftedge.com), so the de_vid
 * cookie rides along with sendBeacon; the id is also carried in the body for
 * browsers that block third-party-context cookies.
 */
const JOURNEY_ENDPOINT = 'https://draftedge.com/api/t.php';

function readCookie(name: string): string {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m && m[1] ? decodeURIComponent(m[1]) : '';
}

function visitorId(): string {
  let v = readCookie('de_vid');
  if (/^[A-Za-z0-9]{20,32}$/.test(v)) return v;
  const a = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const buf = new Uint8Array(26);
  window.crypto.getRandomValues(buf);
  v = Array.from(buf, (b) => a[b % 62]).join('');
  try {
    document.cookie = `de_vid=${v};path=/;max-age=${400 * 86400};domain=.draftedge.com;secure;samesite=Lax`;
  } catch {
    /* cookie blocked: the id still rides in the body */
  }
  return v;
}

export function journey(kind: 'page' | 'cta' | 'event', name: string, params?: Record<string, unknown>): void {
  if (!analyticsEnabled()) return;
  try {
    if (!/^[a-z0-9_]{1,64}$/.test(name)) return;
    const body = JSON.stringify({ n: name, k: kind, p: window.location.pathname, v: visitorId(), x: params });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(JOURNEY_ENDPOINT, new Blob([body], { type: 'text/plain' }));
    } else {
      void fetch(JOURNEY_ENDPOINT, { method: 'POST', body, credentials: 'include', keepalive: true, mode: 'no-cors' }).catch(() => {});
    }
  } catch {
    /* a beacon failure is never an app failure */
  }
}

/** Fire a manual page_view for a client-side route change. */
export function trackPageView(path: string, title?: string): void {
  journey('page', 'page_view', { title: (title ?? document.title).slice(0, 120) });
  if (!analyticsEnabled() || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}

/** Fire a custom event. No-ops safely when analytics is disabled. */
export function track(name: string, params?: Record<string, unknown>): void {
  journey(name === 'cta_click' ? 'cta' : 'event', name, params);
  if (!analyticsEnabled() || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params ?? {});
}
